import React from 'react';

import { useDispatch, useSelector } from 'react-redux';

import IconWithFallback from 'src/components/icon-with-fallback';
import { LoadingStatus } from 'src/constants/loading-status';
import { useIntl } from 'src/hooks/intl';
import {
  changeFilterState,
  fetchJiraRelevantProjects,
} from 'src/redux/jira/actions';
import {
  getFilterState,
  getProjectAssociations,
  getRelevantProjectFetchedStatus,
} from 'src/redux/jira/selectors/repo-page-selectors';

import { MultiSelectFilterDropdown } from '../filter-dropdown';

import messages from './relevant-projects-filter.i18n';

export const RelevantProjectsFilter = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { projectIds, siteCloudId } = useSelector(getFilterState);
  const relevantProjects = useSelector(getProjectAssociations)
    .map(projectAssociation => projectAssociation.project)
    .filter(project => project.site.cloudId === siteCloudId);
  const relevantProjectsFetchedStatus: LoadingStatus = useSelector(
    getRelevantProjectFetchedStatus
  );

  // Project filter is considered loaded when:
  //   - relevant projects are fetched
  //   - relevant sites are loaded and one of them is selected
  //     so that we can filter projects by that site
  const isLoading =
    !siteCloudId || relevantProjectsFetchedStatus === LoadingStatus.Fetching;
  const isFailed = relevantProjectsFetchedStatus === LoadingStatus.Failed;

  const options = relevantProjects.map(project => {
    const avatarUrl = project.avatarUrls['48x48'];
    return {
      label: project.name,
      value: project.id,
      avatar: (
        <IconWithFallback
          key={avatarUrl}
          src={avatarUrl}
          size="xsmall"
          appearance="circle"
        />
      ),
    };
  });

  const selected = options
    .filter(option => projectIds.some(id => id === option.value))
    .map(p => p.value);

  const handleChange = (selectedProjects: string[]) => {
    dispatch(
      changeFilterState({
        projectIds: selectedProjects
          ? selectedProjects.map(project => String(project))
          : [],
      })
    );
  };

  return (
    <MultiSelectFilterDropdown
      isLoading={isLoading}
      label={intl.formatMessage(messages.relevantProjectsDropdownTrigger)}
      onChange={handleChange}
      options={options}
      optionsTitle={intl.formatMessage(
        messages.relevantProjectsDropdownHeading,
        { count: relevantProjects.length }
      )}
      selected={selected}
      errorMessage={isFailed ? intl.formatMessage(messages.errorMessage) : ''}
      onRetry={() => dispatch(fetchJiraRelevantProjects())}
    />
  );
};
